import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchV2Schedules,
  fetchChangelog,
  rollbackBatch,
  rollbackChange,
  getExportXerUrl,
} from "../../../api/clientV2";
import type { ChangeEvent } from "../../../models";
import Spinner from "../../../components/Spinner/Spinner";
import { formatDateTime } from "../../../utils/format";
import "./ChangelogView.css";

const ChangelogView: React.FC = () => {
  const [scheduleId, setScheduleId] = useState<number | null>(null);
  const [events, setEvents] = useState<ChangeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [reverting, setReverting] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    fetchV2Schedules().then((s) => {
      if (s.length > 0) setScheduleId(s[0].id);
    });
  }, []);

  const loadChangelog = useCallback(async () => {
    if (scheduleId == null) return;
    setLoading(true);
    try {
      const data = await fetchChangelog(scheduleId);
      setEvents(data);
      hasLoadedRef.current = true;
    } finally {
      setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => {
    loadChangelog();
  }, [loadChangelog]);

  // Reverted-detection. For activity-scoped rows the key is
  // `(activity_id, field_name)` — last writer wins per pair, and an event is
  // reverted iff every pair's latest writer is a rollback from a different
  // event. For project-scoped rows (activity_id == null), the field is
  // single-valued and would collapse all events' pairs together; instead we
  // match each row against later rollback rows whose old/new values are the
  // inverse swap.
  const revertedKeys = useMemo(() => {
    const sorted = [...events].sort((a, b) =>
      (a.changed_at || "").localeCompare(b.changed_at || ""),
    );

    type LatestWriter = { eventKey: string; isRollback: boolean };
    const latestByPair = new Map<string, LatestWriter>();

    type ProjectRollback = {
      old_value: string | null;
      new_value: string | null;
      eventKey: string;
      changedAt: string;
    };
    const projectRollbacks: ProjectRollback[] = [];

    for (const e of sorted) {
      const k = eventKey(e);
      const isRollback = e.change_type === "rollback";
      for (const c of e.children) {
        if (c.activity_id == null) {
          if (isRollback) {
            projectRollbacks.push({
              old_value: c.old_value,
              new_value: c.new_value,
              eventKey: k,
              changedAt: e.changed_at || "",
            });
          }
          continue;
        }
        latestByPair.set(`${c.activity_id}::${c.field_name}`, {
          eventKey: k,
          isRollback,
        });
      }
    }

    const result = new Set<string>();
    for (const e of events) {
      if (e.change_type === "rollback") continue;
      if (e.children.length === 0) continue;
      const k = eventKey(e);
      const allReverted = e.children.every((c) => {
        if (c.activity_id == null) {
          return projectRollbacks.some(
            (rb) =>
              rb.new_value === c.old_value &&
              rb.old_value === c.new_value &&
              rb.changedAt > (e.changed_at || "") &&
              rb.eventKey !== k,
          );
        }
        const latest = latestByPair.get(`${c.activity_id}::${c.field_name}`);
        return latest != null && latest.isRollback && latest.eventKey !== k;
      });
      if (allReverted) result.add(k);
    }
    return result;
  }, [events]);

  const handleRevert = useCallback(
    async (event: ChangeEvent) => {
      if (scheduleId == null) return;
      const key = eventKey(event);
      setReverting(key);
      try {
        if (event.batch_id) {
          await rollbackBatch(scheduleId, event.batch_id);
        } else if (event.children.length === 1) {
          // Legacy NULL-batch_id row: fall back to per-row rollback so old
          // single-field events are still revertable from the UI.
          await rollbackChange(scheduleId, event.children[0].id);
        }
        await loadChangelog();
      } finally {
        setReverting(null);
      }
    },
    [scheduleId, loadChangelog],
  );

  const toggleExpanded = (event: ChangeEvent) => {
    const key = eventKey(event);
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const fromSurveys = events.filter((e) =>
    e.change_type.startsWith("survey"),
  ).length;
  const manual = events.filter((e) =>
    e.change_type.startsWith("manual") || e.change_type === "work_code_assign",
  ).length;

  return (
    <div className="changelog-view">
      <Spinner visible={loading} />
      <div className="changelog-header">
        <span className="changelog-title">Change Log</span>
        {scheduleId && (
          <a
            className="changelog-export-btn"
            href={getExportXerUrl(scheduleId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Export Modified XER
          </a>
        )}
      </div>

      <div className="changelog-summary">
        <span>{events.length} events total</span>
        <span>{fromSurveys} from surveys</span>
        <span>{manual} manual edits</span>
      </div>

      {hasLoadedRef.current && events.length === 0 ? (
        <div className="changelog-empty">
          No changes recorded yet. Use the Survey or Takt board to make
          mutations.
        </div>
      ) : (
        <table className="changelog-table">
          <thead>
            <tr>
              <th></th>
              <th>Time</th>
              <th>Type</th>
              <th>Summary</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {events.flatMap((e) => {
              const key = eventKey(e);
              const isOpen = expanded.has(key);
              const isReverted = revertedKeys.has(key);
              // Legacy NULL-batch_id rows are revertable via the per-row
              // fallback in handleRevert (singleton children only).
              const canRevert =
                e.change_type !== "rollback" &&
                !isReverted &&
                (e.batch_id != null || e.children.length === 1);

              const rows: ReactNode[] = [
                <tr
                  key={`event-${key}`}
                  className={
                    "cl-event-row" + (isReverted ? " cl-row-reverted" : "")
                  }
                  onClick={() => toggleExpanded(e)}
                >
                  <td className="cl-caret">{isOpen ? "▾" : "▸"}</td>
                  <td>{formatDateTime(e.changed_at)}</td>
                  <td>
                    <span className={`cl-type cl-type-${e.change_type}`}>
                      {e.change_type.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="cl-summary">{e.summary}</td>
                  <td>
                    {canRevert && (
                      <button
                        className="cl-revert-btn"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          handleRevert(e);
                        }}
                        disabled={reverting === key}
                      >
                        {reverting === key ? "..." : "Revert"}
                      </button>
                    )}
                  </td>
                </tr>,
              ];

              if (isOpen) {
                rows.push(
                  <tr
                    key={`children-${key}`}
                    className={
                      "cl-children-row" + (isReverted ? " cl-row-reverted" : "")
                    }
                  >
                    <td></td>
                    <td colSpan={4}>
                      <table className="cl-children-table">
                        <thead>
                          <tr>
                            <th>Activity</th>
                            <th>Field</th>
                            <th>Old</th>
                            <th>New</th>
                          </tr>
                        </thead>
                        <tbody>
                          {e.children.map((c) => (
                            <tr key={c.id}>
                              <td>
                                {c.activity_id == null
                                  ? <span className="cl-project-scoped">Project-scoped</span>
                                  : (c.task_code || c.activity_id)}
                              </td>
                              <td>{c.field_name}</td>
                              <td className="cl-old-value">
                                {c.old_value || "—"}
                              </td>
                              <td className="cl-new-value">
                                {c.new_value || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>,
                );
              }
              return rows;
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

function eventKey(event: ChangeEvent): string {
  return event.batch_id ?? `row-${event.children[0]?.id ?? "unknown"}`;
}

export default ChangelogView;
